import type { Garden, Plant } from '@modules/garden-planner/business_logic';
import { getPlantById } from '@modules/garden-planner/business_logic';

/** Canonical plant categories from seed/catalog, in display order. */
const CATEGORY_ORDER = ['Fruits', 'Vegetables', 'Herbs', 'Flowers'] as const;

export interface PlantSummaryItem {
  plantId: string;
  name: string;
  speciesName?: string;
  emoji: string;
  imagePath?: string;
  count: number;
}

export interface PlantCategoryGroup {
  category: string;
  plants: PlantSummaryItem[];
}

export function hasPlantsInGarden(plantCounts: Record<string, number>) {
  return Object.entries(plantCounts).filter(([, count]) => count > 0).length > 0;
}

/** Available cells with no plants placed (ignores multi-slot capacity per cell). */
export function getFreeCellCount(selectedGarden: Garden | null | undefined) {
  if (!selectedGarden) return 0;

  const occupiedCells = new Set(selectedGarden.plants.map((plant) => `${plant.row},${plant.col}`));

  return (selectedGarden.availableCells || []).filter(
    (cell) => !occupiedCells.has(`${cell.row},${cell.col}`)
  ).length;
}

function categorySortIndex(category: string): number {
  const index = CATEGORY_ORDER.indexOf(category as (typeof CATEGORY_ORDER)[number]);
  return index === -1 ? CATEGORY_ORDER.length : index;
}

/**
 * Groups planted counts by each plant's stored `category`.
 * Unknown/other categories sort after the canonical four, alphabetically.
 */
export function groupPlantCountsByCategory(
  plantCounts: Record<string, number>,
  plants: Plant[]
): PlantCategoryGroup[] {
  const byCategory = new Map<string, PlantSummaryItem[]>();

  for (const [plantId, count] of Object.entries(plantCounts)) {
    if (count <= 0) continue;
    const plant = getPlantById(plants, plantId);
    if (!plant) continue;

    const items = byCategory.get(plant.category) ?? [];
    items.push({
      plantId,
      name: plant.name,
      speciesName: plant.speciesName,
      emoji: plant.emoji,
      imagePath: plant.imagePath,
      count,
    });
    byCategory.set(plant.category, items);
  }

  for (const items of byCategory.values()) {
    items.sort((a, b) => {
      const speciesA = a.speciesName ?? a.name;
      const speciesB = b.speciesName ?? b.name;
      return speciesA.localeCompare(speciesB) || a.name.localeCompare(b.name);
    });
  }

  return [...byCategory.entries()]
    .sort(([a], [b]) => {
      const orderDiff = categorySortIndex(a) - categorySortIndex(b);
      if (orderDiff !== 0) return orderDiff;
      return a.localeCompare(b);
    })
    .map(([category, plantsInCategory]) => ({
      category,
      plants: plantsInCategory,
    }));
}
